import { useCallback, useEffect, useState } from 'react';
import {
  endMaintenance,
  fetchMaintenance,
  formatWib,
  startMaintenance,
  type SystemMaintenance,
} from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Saklar pemeliharaan aplikasi & storage (§D10.1).
 *
 * **Dua jenis, bukan satu saklar**, karena yang dijawab dua pertanyaan berbeda —
 * dan menyatukannya berarti mematikan seluruh sistem untuk mengganti satu disk.
 */
export function MaintenanceSwitch() {
  const { t } = useBahasa();
  const [aktif, setAktif] = useState<SystemMaintenance | null>(null);
  const [scope, setScope] = useState<'app' | 'storage'>('app');
  const [pesan, setPesan] = useState('');
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);

  const muat = useCallback(async () => {
    try {
      setAktif(await fetchMaintenance());
    } catch (e) {
      setGalat((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void muat();
  }, [muat]);

  if (aktif) {
    return (
      <div>
        <p className="rounded-md border border-accent px-3 py-2 text-sm">
          <strong>
            {aktif.scope === 'storage'
              ? t('Storage maintenance is on — recording is paused on purpose.')
              : t('Application maintenance is on.')}
          </strong>
          <span className="mt-1 block">{aktif.message}</span>
          <span className="mt-1 block text-xs text-muted">
            {t('since {waktu}, by {siapa}', { waktu: formatWib(aktif.since), siapa: aktif.by })}
          </span>
        </p>
        {galat && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {galat}
          </p>
        )}
        <button
          type="button"
          disabled={sibuk}
          onClick={() => {
            setSibuk(true);
            void endMaintenance()
              .then(muat)
              .catch((e) => setGalat((e as Error).message))
              .finally(() => setSibuk(false));
          }}
          className="mt-3 min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-60"
        >
          {sibuk ? t('Working…') : t('End maintenance')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-2 text-sm">
        {/* Perbedaannya ditulis di layar, bukan diserahkan ke ingatan: yang satu
            tidak menyentuh perekaman sama sekali, yang satu menghentikannya. */}
        <label className="flex items-start gap-2">
          <input
            type="radio"
            className="mt-1"
            checked={scope === 'app'}
            onChange={() => setScope('app')}
          />
          <span>
            <strong>{t('Application')}</strong>
            <span className="block text-xs text-muted">
              {t('Lensa is being deployed or reworked. Recording keeps running — only the pages close, and only for non-admins.')}
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            className="mt-1"
            checked={scope === 'storage'}
            onChange={() => setScope('storage')}
          />
          <span>
            <strong>{t('Storage')}</strong>
            <span className="block text-xs text-muted">
              {t('The recording volume is being worked on. Recording stops on purpose; live view keeps working, and the gap gets a reason written into every camera’s log.')}
            </span>
          </span>
        </label>
      </div>

      <label className="mt-3 block text-sm">
        {t('What is being done')}
        <input
          value={pesan}
          onChange={(e) => setPesan(e.target.value)}
          maxLength={500}
          placeholder={t('e.g. Migrating the recording volume to the new NAS')}
          className="mt-1 min-h-tap w-full rounded-md border border-line bg-page px-3 text-base"
        />
        {/* §D10.1 no.3 — kalimat kosong membuat orang menelepon, dan yang
            ditelepon adalah orang yang sedang mengerjakannya. */}
        <span className="mt-1 block text-xs text-muted">
          {t('Everyone sees this sentence. “Under maintenance, try again later” is what makes people phone you.')}
        </span>
      </label>

      {galat && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {galat}
        </p>
      )}

      <button
        type="button"
        disabled={sibuk || pesan.trim() === ''}
        onClick={() => {
          setSibuk(true);
          void startMaintenance(scope, pesan.trim())
            .then(muat)
            .catch((e) => setGalat((e as Error).message))
            .finally(() => setSibuk(false));
        }}
        className="mt-3 min-h-tap rounded-md bg-accent px-4 text-base text-accent-contrast disabled:opacity-50"
      >
        {sibuk ? t('Working…') : t('Start maintenance')}
      </button>
    </div>
  );
}
